import type { Dispatch } from "@reduxjs/toolkit"

import { type RootState } from "../../../../store.ts"
import {
  type Attachment,
  AttachmentModels,
  type AttachmentParentModels
} from "../../../Album/types/attachment.types.ts"
import { projectSetProp } from "../project.slice.ts"
import { type ProjectState } from "../project.types.ts"

type AttachmentAction = "add" | "remove"

const MODEL_TO_KEY: Partial<
  Record<
    AttachmentModels,
    keyof Pick<
      RootState["project"],
      "checklistItems" | "milestones" | "invoices" | "financialTransactionItems"
    >
  >
> = {
  [AttachmentModels.CHECKLIST_ITEM]: "checklistItems",
  [AttachmentModels.MILESTONE]: "milestones",
  [AttachmentModels.INVOICE]: "invoices",
  [AttachmentModels.FINANCIAL_ITEM]: "financialTransactionItems"
}

export const updateModelAttachments = (
  dispatch: Dispatch,
  state: RootState,
  params: {
    object_id: string
    model_type: AttachmentModels
    parent_type?: AttachmentParentModels
    action: AttachmentAction
    attachment?: Attachment
    attachment_id?: string
  }
) => {
  const {
    object_id,
    model_type,
    parent_type,
    action,
    attachment,
    attachment_id
  } = params

  const applyAttachments = (attachments: Attachment[] = []): Attachment[] =>
    action === "add"
      ? [...attachments, attachment!]
      : attachments.filter((a) => a.id !== attachment_id)

  if (model_type === AttachmentModels.PARTIAL_TRANSACTION_ITEM) {
    const collection = state?.project?.financialTransactionItems
    if (!collection?.results) return
    dispatch(
      projectSetProp({
        key: "financialTransactionItems",
        value: {
          ...collection,
          results: collection.results.map((item) => ({
            ...item,
            partial_finances: item.partial_finances?.map((payment) =>
              payment.id === object_id
                ? {
                    ...payment,
                    attachments: applyAttachments(payment.attachments)
                  }
                : payment
            )
          }))
        } as ProjectState["financialTransactionItems"]
      })
    )
    return
  }

  const key = MODEL_TO_KEY[model_type]
  if (!key) return

  const updateAttachments = (attachments: Attachment[] = []): Attachment[] =>
    action === "add"
      ? [...attachments, attachment!]
      : attachments.filter((a) => a.id !== attachment_id)

  if (!parent_type) {
    dispatch(
      projectSetProp({
        key,
        value: {
          ...state?.project?.[key],
          results: state?.project?.[key]?.results?.map((item) =>
            item.id === object_id
              ? { ...item, attachments: updateAttachments(item.attachments) }
              : item
          )
        } as ProjectState[typeof key]
      })
    )
    return
  }

  switch (parent_type) {
    case "milestone":
      return dispatch(
        projectSetProp({
          key: "milestones",
          value: {
            ...state.project.milestones,
            results: state.project.milestones.results.map((milestone) => ({
              ...milestone,
              financial_items: milestone.financial_items?.map((item) =>
                item.id === object_id
                  ? {
                      ...item,
                      attachments: updateAttachments(item.attachments)
                    }
                  : item
              )
            }))
          }
        })
      )
  }
}
